import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../services/api'
import { useFormValidation, ValidationSchema } from './useFormValidation'
import { useDataFetching } from './useDataFetching'

/**
 * Shared boilerplate for simple admin reference-data forms (BDM, Region, ...).
 *
 * Composes `useFormValidation` (client validation + submit) with `useDataFetching`
 * (edit-mode load) and owns the `error` state + edit/create PUT/POST routing that
 * every such form repeated verbatim. Form-specific concerns (field mapping, extra
 * dependent fetches, market-driven resets) stay in the component.
 */
export interface UseSimpleAdminFormOptions<TForm> {
  /** True when editing an existing record (drives PUT vs POST + load-on-mount). */
  isEditMode: boolean
  /** Collection path, e.g. '/admin/bdm' or '/admin/regions'. */
  resourcePath: string
  /** Record id / code from the route; required in edit mode. */
  resourceId?: string
  /** Path to navigate to after a successful save. */
  listPath: string
  initialValues: TForm
  validationSchema?: ValidationSchema
  /** Maps form values to the request body sent to the API. */
  toPayload: (values: TForm) => unknown
  /** Maps a loaded record onto form values (edit mode). */
  fromResponse: (data: any) => TForm
  /** Fallback message when a save fails. */
  saveErrorMessage: string
  /** Fallback message when the edit-mode load fails. */
  loadErrorMessage: string
}

export function useSimpleAdminForm<TForm extends Record<string, any>>(
  options: UseSimpleAdminFormOptions<TForm>,
) {
  const {
    isEditMode,
    resourcePath,
    resourceId,
    listPath,
    initialValues,
    validationSchema,
    toPayload,
    fromResponse,
    saveErrorMessage,
    loadErrorMessage,
  } = options

  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  const form = useFormValidation<TForm>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setError(null)
      try {
        if (isEditMode) {
          await apiClient.put(`${resourcePath}/${resourceId}`, toPayload(values))
        } else {
          await apiClient.post(resourcePath, toPayload(values))
        }
        navigate(listPath)
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || saveErrorMessage)
      }
    },
  })

  // Edit mode: load the record and populate the form.
  const { loading } = useDataFetching({
    fetchFn: async () => {
      const response = await apiClient.get(`${resourcePath}/${resourceId}`)
      return response.data
    },
    dependencies: [resourceId],
    immediate: isEditMode,
    onSuccess: (data) => form.setValues(fromResponse(data)),
    onError: (err: any) =>
      setError(err.response?.data?.detail || err.message || loadErrorMessage),
  })

  return {
    values: form.values,
    errors: form.errors,
    handleChange: form.handleChange,
    handleBlur: form.handleBlur,
    handleSubmit: form.handleSubmit,
    setFieldValue: form.setFieldValue,
    setValues: form.setValues,
    isSubmitting: form.isSubmitting,
    loading,
    error,
  }
}
